import dragula from "dragula"
import confetti from "canvas-confetti"
import autoScroll from "dom-autoscroller"
const CLIENT_COLUMN = "client"
const CLOSED_COLUMN = "closed"
const DEAD_COLUMN = "dead"
const DEAD_DEALS_CONFIRMATION_POPUP_SELECTOR = "#dead-deals-confirmation-popup"
const LEASES_OUT_COLUMN = "leases_out"
const UNDER_CONTRACT_COLUMN = "under_contract"
const MOBILE_VIEWPORT = "(max-width: 639px)"
const MOBILE_DRAG_HOLD_MS = 350
const MOBILE_EDGE_ZONE_PX = 48
const MOBILE_EDGE_HOLD_MS = 300
const MOBILE_EDGE_REPEAT_MS = 650
const MOBILE_SNAP_DROP_TRACK_MS = 600
const CARD_CLICK_SUPPRESSION_MS = 500

export const Board = {
  lastMovement: null,
  lastSibling: null,
  registeredCardClickListeners: {},
  mounted() {
    this.registeredCardClickListeners = {}
    this.autoScroller = null
    this.mobileDragActive = false
    this.mobileDragArmed = false
    this.suppressCardClicksUntil = 0
    this.handleEvent("kanban:restore_last_drag", ({ id }) => {
      restoreLastDrag(this.lastMovement, id)
    })

    this.handleEvent("highlight:new_kanban_card_comment", (params) => {
      let cardComment = document.getElementById(
        `card-comment-${params.comment_id}`,
      )
      cardComment?.classList.add("highlighted")
    })

    this.handleEvent("highlight:new_kanban_card_details", (params) => {
      // If in :dead stage, card might not be present in board
      let card = document.getElementById(`card-box-${params.item_id}`)

      // https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Animations/Tips#run_an_animation_again
      card?.classList.remove("highlighted")

      window.requestAnimationFrame(function (time) {
        window.requestAnimationFrame(function (time) {
          card?.classList.add("highlighted")
        })
      })
    })

    window.addEventListener("board:celebrate", (params) => {
      if (params.target.value == CLOSED_COLUMN) showConfetti()
    })

    this.initDragula()
    this.setupLongPress()
    this.assignClickHandlers()
    this.manageDeadDealsConfirmationDialog()

    // Block card dragging when changing rep type view
    document.querySelectorAll(".filter-input").forEach((radio) => {
      radio.addEventListener("change", () => {
        this.destroyDragAndScroll()
      })
    })
  },
  updated() {
    // Dragula must be re-initialized so that it updates it's bindings
    // when liveview updates the columns
    this.destroyDragAndScroll()
    this.initDragula()
    this.assignClickHandlers()
  },
  destroyed() {
    this.destroyDragAndScroll()
    this.teardownLongPress()
  },
  destroyDragAndScroll() {
    this.finishMobileDrag()
    this.cancelMobileLongPress()
    this.drake?.destroy()
    this.drake = null
    this.autoScroller?.destroy(true)
    this.autoScroller = null
  },
  initDragula() {
    const containers = document.querySelectorAll("[data-js-draggable]")
    const board = this
    this.dragOptions = {
      moves: function (el, source, handle, sibling) {
        return (
          el.hasAttribute("data-js-draggable-id") &&
          (!board.isMobileViewport() || board.mobileDragArmed)
        )
      },
    }
    const drake = dragula(Array.from(containers), this.dragOptions)

    // Mobile uses discrete, one-column edge navigation below. Keep the generic
    // auto-scroller for vertically scrolling desktop columns only.
    this.autoScroller = autoScroll(Array.from(containers), {
      margin: 40,
      maxSpeed: 24,
      scrollWhenOutside: true,
      autoScroll: function () {
        return this.down && drake.dragging && !board.isMobileViewport()
      },
    })
    const deadContainer = [...containers].find(
      (container) => container.dataset.queue == DEAD_COLUMN,
    )
    drake.on("drag", (el) => {
      this.lastSibling = el.nextElementSibling
      this.startMobileDrag(el)

      if (deadContainer) {
        deadContainer.classList.remove("opacity-0")
        deadContainer.classList.add("opacity-100")
      }
    })
    drake.on("dragend", () => {
      this.finishMobileDrag()
    })
    drake.on("cancel", () => {
      if (deadContainer) {
        deadContainer.classList.remove("opacity-100")
        deadContainer.classList.add("opacity-0")
      }
    })

    drake.on("drop", (el, targetContainer, sourceContainer, sibling) => {
      try {
        if (deadContainer) {
          deadContainer.classList.remove("opacity-100")
          deadContainer.classList.add("opacity-0")
        }

        this.lastMovement = {
          el: el,
          targetContainer: targetContainer,
          sourceContainer: sourceContainer,
          sourceSibling: this.lastSibling,
          sibling: sibling,
        }

        const targetQueueName = targetContainer.dataset.queue

        if (
          targetQueueName === CLIENT_COLUMN &&
          this.el.dataset.repType === "occupier"
        ) {
          const card = this.lastMovement.el
          const targetId = card.dataset.jsDraggableId
          const event = { id: targetId }

          this.pushEventTo(
            this.el.dataset.targetId,
            "dialog:new-client:show",
            event,
          )
        }

        if (
          targetQueueName == LEASES_OUT_COLUMN ||
          targetQueueName == UNDER_CONTRACT_COLUMN
        ) {
          const card = this.lastMovement.el
          const targetId = card.dataset.jsDraggableId
          const event = { id: targetId }

          this.pushEventTo(
            this.el.dataset.targetId,
            "dialog:size-and-commission:show",
            event,
          )
        }

        if (targetQueueName == DEAD_COLUMN) {
          enableShadow()
          drake.cancel(true)

          if (
            this.el.dataset.repType === "landlord" ||
            this.el.dataset.repType === "sublandlord"
          ) {
            const card = this.lastMovement.el
            const targetId = card.dataset.jsDraggableId
            const targetQueueName =
              this.lastMovement.targetContainer.dataset.queue

            const event = {
              id: targetId,
              targetQueue: targetQueueName,
              previousId: null,
            }

            this.pushEventTo(
              this.el.dataset.targetId,
              "show-confirm-dead-deal",
              event,
            )
          } else {
            showDeadDealsConfirmationDialog(el)
          }
        } else {
          const previousSibling = el.previousElementSibling
          const targetId = el.dataset.jsDraggableId
          const previousId =
            (previousSibling && previousSibling.dataset.jsDraggableId) || null

          const event = {
            id: targetId,
            targetQueue: targetQueueName,
            previousId: previousId,
          }

          this.pushEventTo(this.el.dataset.targetId, "move-draggable", event)
        }

        switch (targetQueueName) {
          case CLIENT_COLUMN:
            removeDraggablesFromContainer(targetContainer)
            break
          case CLOSED_COLUMN:
            showConfetti()
            break
        }
      } catch (e) {
        // Let's just log the error
        // and cancel the drag for now
        console.error(e)
        console.log(e.stack)
        drake.cancel(true)
      }
    })

    drake.on("cloned", applyDragStyling)

    drake.on("shadow", (_el, container) => {
      const queue = container.dataset.queue
      const overDead = queue == DEAD_COLUMN
      const overClient = queue == CLIENT_COLUMN

      deadContainer && deadContainer.classList.toggle("active", overDead)

      // The dead and client columns have explicit dashed drop zones, so hide
      // the default card placeholder to avoid stacking two landing hints.
      if (overDead || overClient) {
        disableShadow()
      } else {
        enableShadow()
      }
    })

    this.drake = drake
  },
  isMobileViewport() {
    return window.matchMedia(MOBILE_VIEWPORT).matches
  },
  // Dragula's slideFactor does not gate TouchEvents because TouchEvent itself
  // has no clientX/clientY properties. Keep Dragula out of the gesture until a
  // stationary long press completes, then give it a synthetic mouse-down so
  // the next real touch movement starts the drag. Any movement before the timer
  // completes remains a native board/column scroll.
  setupLongPress() {
    if (this._longPressBound) return
    this._longPressBound = true

    this._onMobileTouchStart = (event) => {
      if (!this.isMobileViewport() || event.touches.length !== 1) return

      const card = event.target.closest("[data-js-draggable-id]")
      if (!card) return

      this.cancelMobileLongPress()
      const touch = event.touches[0]
      const gesture = {
        card,
        clientX: touch.clientX,
        clientY: touch.clientY,
        armed: false,
        timer: null,
      }

      gesture.timer = setTimeout(() => {
        if (this.mobileTouchGesture !== gesture || !card.isConnected) return

        gesture.timer = null
        gesture.armed = true
        this.mobileDragArmed = true
        this.suppressCardClick()
        card.classList.add("drag-armed")
        navigator.vibrate?.(15)
        this.dispatchMobileMouseEvent("mousedown", gesture)
      }, MOBILE_DRAG_HOLD_MS)

      this.mobileTouchGesture = gesture
    }

    this._onMobileTouchMove = (event) => {
      const gesture = this.mobileTouchGesture
      if (!gesture && !this.mobileDragActive) return

      const touch = event.touches[0]
      if (!touch) return

      if (gesture?.armed || this.mobileDragActive) {
        // Capture on window so no ancestor, column, board, or page can consume
        // movement during a drag. The touch sensor owns the sequence after the
        // long press and drives Dragula with mouse events, avoiding Dragula's
        // unreliable TouchEvent coordinate threshold path.
        event.preventDefault()
        event.stopPropagation()
        this.dispatchMobileMouseEvent("mousemove", touch)
        if (this.mobileDragActive) this.handleMobileDragEdge(touch.clientX)
        return
      }

      // A TouchEvent before the hold expires is a scroll gesture. Cancel the
      // pending drag immediately and leave the event untouched for the native
      // horizontal board or vertical column scroller.
      this.suppressCardClick()
      this.cancelMobileLongPress()
    }

    this._onMobileTouchEnd = (event) => {
      if (this.mobileTouchGesture?.armed || this.mobileDragActive) {
        event.preventDefault()
        event.stopPropagation()
        this.dispatchMobileMouseEvent(
          "mouseup",
          event.changedTouches[0] || this.mobileLastTouch,
        )
      }

      this.cancelMobileLongPress()
      this.clearMobileEdgeNavigation()
    }

    this.el.addEventListener("touchstart", this._onMobileTouchStart, {
      passive: true,
    })
    window.addEventListener("touchmove", this._onMobileTouchMove, {
      passive: false,
      capture: true,
    })
    window.addEventListener("touchend", this._onMobileTouchEnd, {
      passive: false,
      capture: true,
    })
    window.addEventListener("touchcancel", this._onMobileTouchEnd, {
      passive: false,
      capture: true,
    })
  },
  teardownLongPress() {
    if (!this._longPressBound) return

    this.el.removeEventListener("touchstart", this._onMobileTouchStart)
    window.removeEventListener("touchmove", this._onMobileTouchMove, true)
    window.removeEventListener("touchend", this._onMobileTouchEnd, true)
    window.removeEventListener("touchcancel", this._onMobileTouchEnd, true)
    this.cancelMobileLongPress()
    this._longPressBound = false
  },
  cancelMobileLongPress() {
    const gesture = this.mobileTouchGesture
    if (gesture?.timer) clearTimeout(gesture.timer)
    gesture?.card.classList.remove("drag-armed")
    this.mobileTouchGesture = null
    this.mobileDragArmed = false
  },
  suppressCardClick() {
    this.suppressCardClicksUntil = Date.now() + CARD_CLICK_SUPPRESSION_MS
  },
  dispatchMobileMouseEvent(type, touch) {
    if (!touch) return

    this.mobileLastTouch = {
      clientX: touch.clientX,
      clientY: touch.clientY,
    }
    const target =
      type === "mousedown"
        ? this.mobileTouchGesture?.card
        : document.elementFromPoint(touch.clientX, touch.clientY)

    target?.dispatchEvent(
      new MouseEvent(type, {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX: touch.clientX,
        clientY: touch.clientY,
        screenX: touch.clientX,
        screenY: touch.clientY,
        button: 0,
        buttons: type === "mouseup" ? 0 : 1,
      }),
    )
  },
  startMobileDrag(card) {
    if (!this.isMobileViewport()) return

    this.mobileDragActive = true
    this.mobileDraggedCard = card
    this.el.dataset.mobileDragging = ""
    this.suppressCardClick()
    this.clearMobileEdgeNavigation()
  },
  finishMobileDrag() {
    if (!this.mobileDragActive && !this.mobileDragArmed) return

    this.suppressCardClick()
    this.clearMobileEdgeNavigation()
    this.mobileDraggedCard?.classList.remove("drag-armed")
    this.mobileDraggedCard = null
    this.mobileDragActive = false
    this.mobileDragArmed = false
    delete this.el.dataset.mobileDragging
  },
  handleMobileDragEdge(clientX) {
    const boardRect = this.el.getBoundingClientRect()
    let direction = 0

    if (clientX <= boardRect.left + MOBILE_EDGE_ZONE_PX) direction = -1
    if (clientX >= boardRect.right - MOBILE_EDGE_ZONE_PX) direction = 1

    if (direction === this.mobileEdgeDirection) return

    this.clearMobileEdgeNavigation()
    if (direction === 0) return

    this.mobileEdgeDirection = direction
    this.mobileEdgeTimer = setTimeout(
      () => this.advanceMobileColumn(direction),
      MOBILE_EDGE_HOLD_MS,
    )
  },
  advanceMobileColumn(direction) {
    if (!this.mobileDragActive || this.mobileEdgeDirection !== direction) return

    const columns = Array.from(
      this.el.querySelectorAll("[data-kanban-column-frame]"),
    )
    const currentIndex = this.visibleMobileColumnIndex(columns)
    const targetIndex = Math.max(
      0,
      Math.min(columns.length - 1, currentIndex + direction),
    )

    if (targetIndex === currentIndex) {
      this.clearMobileEdgeNavigation()
      return
    }

    this.scrollMobileBoardToColumn(columns[targetIndex])
    this.mobileEdgeTimer = setTimeout(
      () => this.advanceMobileColumn(direction),
      MOBILE_EDGE_REPEAT_MS,
    )
  },
  visibleMobileColumnIndex(columns) {
    const boardRect = this.el.getBoundingClientRect()
    const boardCenter = boardRect.left + boardRect.width / 2

    return columns.reduce(
      (closestIndex, column, index) => {
        const rect = column.getBoundingClientRect()
        const distance = Math.abs(rect.left + rect.width / 2 - boardCenter)
        return distance < closestIndex.distance
          ? { index, distance }
          : closestIndex
      },
      { index: 0, distance: Number.POSITIVE_INFINITY },
    ).index
  },
  scrollMobileBoardToColumn(column) {
    const boardRect = this.el.getBoundingClientRect()
    const columnRect = column.getBoundingClientRect()
    const centeredLeft =
      this.el.scrollLeft +
      columnRect.left -
      boardRect.left -
      (this.el.clientWidth - columnRect.width) / 2

    this.el.scrollTo({ left: centeredLeft, behavior: "smooth" })
    this.trackMobileDropTargetDuringSnap()
  },
  trackMobileDropTargetDuringSnap() {
    if (this.mobileDropTargetFrame) {
      cancelAnimationFrame(this.mobileDropTargetFrame)
    }

    const stopAt = performance.now() + MOBILE_SNAP_DROP_TRACK_MS
    const track = () => {
      if (!this.mobileDragActive || !this.mobileLastTouch) return

      this.dispatchMobileMouseEvent("mousemove", this.mobileLastTouch)
      if (performance.now() < stopAt) {
        this.mobileDropTargetFrame = requestAnimationFrame(track)
      } else {
        this.mobileDropTargetFrame = null
      }
    }

    this.mobileDropTargetFrame = requestAnimationFrame(track)
  },
  clearMobileEdgeNavigation() {
    if (this.mobileEdgeTimer) clearTimeout(this.mobileEdgeTimer)
    if (this.mobileDropTargetFrame) {
      cancelAnimationFrame(this.mobileDropTargetFrame)
    }
    this.mobileEdgeTimer = null
    this.mobileEdgeDirection = 0
    this.mobileDropTargetFrame = null
  },
  assignClickHandlers() {
    const cards = this.el.querySelectorAll("[data-js-draggable-id]")
    cards.forEach((item) => {
      const newEventListener = () => {
        if (Date.now() < this.suppressCardClicksUntil) return

        this.pushEventTo(this.el.dataset.targetId, "click-draggable", {
          draggableId: item.dataset.jsDraggableId,
        })
      }
      const eventListener =
        this.registeredCardClickListeners[item.dataset.jsDraggableId] ||
        newEventListener
      this.registeredCardClickListeners[item.dataset.jsDraggableId] =
        eventListener
      item.addEventListener("click", eventListener)
    })
  },

  manageDeadDealsConfirmationDialog() {
    const popup = document.querySelector(DEAD_DEALS_CONFIRMATION_POPUP_SELECTOR)
    if (popup == null) return

    const popupButtons = popup.querySelectorAll("button")
    const acceptButton = popupButtons[0]
    const cancelButton = popupButtons[1]
    acceptButton.addEventListener("click", () => {
      const card = this.lastMovement.el
      const targetId = card.dataset.jsDraggableId
      const targetQueueName = this.lastMovement.targetContainer.dataset.queue
      const event = {
        id: targetId,
        targetQueue: targetQueueName,
        previousId: null,
      }
      this.pushEventTo(this.el.dataset.targetId, "move-draggable", event)
      card.parentElement.removeChild(card)
      hideDeadDealsConfirmationDialog()
    })
    cancelButton.addEventListener("click", () => {
      hideDeadDealsConfirmationDialog()
    })
  },
}

function removeDraggablesFromContainer(container) {
  container
    .querySelectorAll("[data-js-draggable-id]")
    .forEach((draggable) => draggable.parentNode.removeChild(draggable))
}

function restoreLastDrag(movement, draggableId) {
  if (
    !movement?.el ||
    !movement.sourceContainer ||
    movement.el.dataset.jsDraggableId !== draggableId
  ) {
    return
  }

  if (
    movement.sourceSibling &&
    movement.sourceSibling.parentElement === movement.sourceContainer
  ) {
    movement.sourceContainer.insertBefore(movement.el, movement.sourceSibling)
  } else {
    movement.sourceContainer.appendChild(movement.el)
  }
}

function applyDragStyling(_el, _source) {
  const mirroredEl = document.querySelector(".gu-mirror")
  mirroredEl.classList.add("card-draggable")
}
function disableShadow() {
  const shadowEl = document.querySelector(".gu-transit")
  shadowEl.style.display = "none"
}
function enableShadow() {
  const shadowEl = document.querySelector(".gu-transit")
  shadowEl.style.display = "block"
}
function showDeadDealsConfirmationDialog(el) {
  const deadDealsConfirmationPopup = document.querySelector(
    DEAD_DEALS_CONFIRMATION_POPUP_SELECTOR,
  )

  const dealNamePlaceholder = deadDealsConfirmationPopup.querySelector(
    DEAD_DEALS_CONFIRMATION_POPUP_SELECTOR + "-deal-name",
  )
  const dealNameSelector = el.querySelector("[data-kanban-card-title]")
  if (dealNameSelector) {
    const dealName = dealNameSelector.innerText
    dealNamePlaceholder.innerText = dealName
  }
  deadDealsConfirmationPopup.parentElement.style.display = "block"
}
function hideDeadDealsConfirmationDialog() {
  document.querySelector(
    DEAD_DEALS_CONFIRMATION_POPUP_SELECTOR,
  ).parentElement.style.display = "none"
}
function showConfetti() {
  var end = Date.now() + 0.2 * 1000

  ;(function frame() {
    confetti({
      particleCount: 4,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
    })
    confetti({
      particleCount: 4,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
    })

    if (Date.now() < end) {
      requestAnimationFrame(frame)
    }
  })()
}

const shallowCompare = (obj1, obj2) => {
  if (
    (typeof obj1 == "number" && typeof obj2 == "number") ||
    (typeof obj1 == "string" && typeof obj2 == "string")
  ) {
    return obj1 === obj2
  } else {
    return (
      Object.keys(obj1).length === Object.keys(obj2).length &&
      Object.keys(obj1).every(
        (key) => obj2.hasOwnProperty(key) && obj1[key] === obj2[key],
      )
    )
  }
}
